// Copyright (C) 2017-2023 Smart code 203358507

const React = require('react');
const classnames = require('classnames');
const useTranslate = require('stremio/common/useTranslate');
const { default: useVisibleCatalogs } = require('stremio/common/useVisibleCatalogs');
const { default: Icon } = require('@stremio/stremio-icons/react');
const { withCoreSuspender } = require('stremio/common');
const { Image, MainNavBars, MetaItem, MetaRow } = require('stremio/components');
const useSearch = require('./useSearch');
const styles = require('./styles');
const { useSearchParams } = require('react-router-dom');
const { hideUnapprovedCatalogs, isAllowedCatalog, loadAllowedCatalogRange } = require('../../caroline/catalogs/catalogPolicy');
const { classifyCatalog, summarizeCatalogs } = require('./searchState');

const Search = () => {
    const [queryParams] = useSearchParams();
    const t = useTranslate();
    const [search, loadSearchRows] = useSearch(queryParams);
    const allowedCatalogs = React.useMemo(() => hideUnapprovedCatalogs(search.catalogs), [search.catalogs]);
    const catalogsRef = React.useRef(search.catalogs);
    catalogsRef.current = search.catalogs;
    const loadAllowedCatalogRows = React.useCallback((range) => {
        loadAllowedCatalogRange(catalogsRef.current, range, loadSearchRows);
    }, [loadSearchRows]);
    const hasAllowedCatalogs = search.catalogs.some(isAllowedCatalog);
    const queryParam = queryParams.get('search') ?? queryParams.get('query');
    const query = typeof queryParam === 'string' && queryParam.length > 0 ? queryParam : null;
    const [slowStatusQuery, setSlowStatusQuery] = React.useState(null);
    React.useEffect(() => {
        setSlowStatusQuery(null);

        if (query === null) {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => setSlowStatusQuery(query), 8000);
        return () => window.clearTimeout(timeoutId);
    }, [query]);
    const searchSummary = React.useMemo(() => summarizeCatalogs(allowedCatalogs), [allowedCatalogs]);
    const { catalogRows, scrollContainerRef, onScroll } = useVisibleCatalogs({
        catalogs: allowedCatalogs,
        loadRange: loadAllowedCatalogRows,
    });
    return (
        <MainNavBars className={styles['search-container']} route={'search'} query={query}>
            <div ref={scrollContainerRef} className={styles['search-content']} onScroll={onScroll}>
                {
                    query === null ?
                        <div className={classnames(styles['search-hints-wrapper'])}>
                            <div className={classnames(styles['search-hints-title-container'], 'animation-fade-in')}>
                                <div className={styles['search-hints-title']}>{t.string('SEARCH_ANYTHING')}</div>
                            </div>
                            <div className={classnames(styles['search-hints-container'], 'animation-fade-in')}>
                                <div className={styles['search-hint-container']}>
                                    <Icon className={styles['icon']} name={'trailer'} />
                                    <div className={styles['label']}>{t.string('SEARCH_CATEGORIES')}</div>
                                </div>
                                <div className={styles['search-hint-container']}>
                                    <Icon className={styles['icon']} name={'actors'} />
                                    <div className={styles['label']}>{t.string('SEARCH_PERSONS')}</div>
                                </div>
                                <div className={styles['search-hint-container']}>
                                    <Icon className={styles['icon']} name={'link'} />
                                    <div className={styles['label']}>{t.string('SEARCH_PROTOCOLS')}</div>
                                </div>
                                <div className={styles['search-hint-container']}>
                                    <Icon className={styles['icon']} name={'imdb-outline'} />
                                    <div className={styles['label']}>{t.string('SEARCH_TYPES')}</div>
                                </div>
                            </div>
                        </div>
                        :
                        !hasAllowedCatalogs ?
                            <div className={styles['message-container']}>
                                <Image
                                    className={styles['image']}
                                    src={require('/assets/images/empty.png')}
                                    alt={' '}
                                />
                                <div className={styles['message-label']}>{ t.string('STREMIO_TV_SEARCH_NO_ADDONS') }</div>
                            </div>
                            :
                            <div className={styles['search-results-container']}>
                                {searchSummary.isEmpty ? (
                                    <div className={styles['message-container']} role="status">
                                        <Image
                                            className={styles['image']}
                                            src={require('/assets/images/empty.png')}
                                            alt={' '}
                                        />
                                        <div className={styles['message-label']}>{t.string('SEARCH_NO_RESULTS')}</div>
                                    </div>
                                ) : catalogRows.map(({ catalog, index }) => {
                                    const catalogState = classifyCatalog(catalog);
                                    switch (catalogState.status) {
                                        case 'results': {
                                            const posterShape = catalogState.items[0]?.posterShape || 'poster';
                                            return (
                                                <MetaRow
                                                    key={index}
                                                    className={classnames(styles['search-row'], styles[`search-row-${posterShape}`], 'animation-fade-in')}
                                                    catalog={catalog}
                                                    itemComponent={MetaItem}
                                                />
                                            );
                                        }
                                        case 'empty': {
                                            return null;
                                        }
                                        case 'error': {
                                            return (
                                                <MetaRow
                                                    key={index}
                                                    className={classnames(styles['search-row'], 'animation-fade-in')}
                                                    catalog={catalog}
                                                    message={t.string('GENERIC_ERROR_MESSAGE')}
                                                />
                                            );
                                        }
                                        case 'pending': {
                                            if (slowStatusQuery === query) {
                                                return (
                                                    <MetaRow
                                                        key={index}
                                                        className={classnames(styles['search-row'], 'animation-fade-in')}
                                                        catalog={catalog}
                                                        message={t.stringWithPrefix(
                                                            'SEARCH_SLOW',
                                                            'CAROLINE_',
                                                            'A pesquisa está a demorar mais do que o esperado. Os resultados podem surgir mais tarde.'
                                                        )}
                                                    />
                                                );
                                            }
                                            return (
                                                <MetaRow.Placeholder
                                                    key={index}
                                                    className={classnames(styles['search-row'], styles['search-row-poster'], 'animation-fade-in')}
                                                    catalog={catalog}
                                                    title={t.catalogTitle(catalog)}
                                                />
                                            );
                                        }
                                        default: {
                                            return null;
                                        }
                                    }
                                })}
                            </div>
                }
            </div>
        </MainNavBars>
    );
};

const SearchFallback = () => {
    const [queryParams] = useSearchParams();
    return <MainNavBars className={styles['search-container']} route={'search'} query={queryParams.get('search') ?? queryParams.get('query')} />;
};

module.exports = withCoreSuspender(Search, SearchFallback);
